import {alphaColor, mixColor, rgbChannels, type CampaignPalette} from "./campaignPalette";
import {HALLOWEEN_EDITIONS, type CampaignId} from "./campaignPlayback";
import {drawInk, drawShowcase, drawKinetic, drawCutpaper, drawCollage, studyGeometry, type CampaignArtworkGeometry} from "./campaignStudyRenderers";
import {editionSlide} from "./halloweenPainters";
import {fontFamilies, topbar} from "theme/tokens";

const TAU = Math.PI * 2;

function shaded(base: number[], light: number, alpha = 1) {
	return `rgba(${base.map(value => Math.round(Math.min(255, value * light))).join(',')},${alpha})`;
}

function petal(ctx: CanvasRenderingContext2D, angle: number, radius: number, width: number, bend: number, brightness: number, p: CampaignPalette) {
	ctx.save(); ctx.rotate(angle);
	const gradient = ctx.createLinearGradient(0, 0, radius, width * .45);
	gradient.addColorStop(0, mixColor(p.gold, p.shadow, 0.790));
	gradient.addColorStop(.22, shaded(rgbChannels(p.gold), brightness * .70));
	gradient.addColorStop(.60, shaded(rgbChannels(p.gold), brightness * 1.05));
	gradient.addColorStop(.87, shaded(rgbChannels(p.gold), brightness * .96));
	gradient.addColorStop(1, mixColor(p.white, p.gold, 0.451));
	ctx.beginPath();
	ctx.moveTo(0, -width * .12);
	ctx.bezierCurveTo(radius * .35, -width * .85, radius * .88, -width + bend, radius, bend);
	ctx.bezierCurveTo(radius * 1.01, width * .43 + bend, radius * .38, width * .48, 0, width * .10);
	ctx.closePath(); ctx.fillStyle = gradient;
	ctx.shadowColor = alphaColor(p.shadow, 0.627); ctx.shadowBlur = width * .12; ctx.shadowOffsetY = width * .06; ctx.fill();
	ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
	const fold = ctx.createLinearGradient(0, 0, radius, 0);
	fold.addColorStop(0, alphaColor(mixColor(p.gold, p.white, 0.543), 0.000)); fold.addColorStop(.6, alphaColor(mixColor(p.gold, p.white, 0.543), 0.361)); fold.addColorStop(1, alphaColor(mixColor(p.gold, p.white, 0.543), 0.078));
	ctx.beginPath(); ctx.moveTo(0,0); ctx.bezierCurveTo(radius*.34, -width*.18, radius*.77, -width*.3+bend, radius,bend);
	ctx.strokeStyle = fold; ctx.lineWidth = 1.5; ctx.stroke();
	ctx.restore();
}

function drawBloom(ctx: CanvasRenderingContext2D, w: number, h: number, scale: number, p: CampaignPalette) {
	ctx.fillStyle = p.ground; ctx.fillRect(0, 0, w, h);
	const mobile = w <= 900;
	const cx = w * (mobile ? 1.15 : 1.10), cy = h * (mobile ? .68 : .47);
	const radius = (mobile ? w * .79 : Math.max(h * .59, w * .34)) * scale;
	const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * 1.1);
	glow.addColorStop(0, alphaColor(mixColor(p.gold, p.shadow, 0.624), 0.149)); glow.addColorStop(1, alphaColor(p.shadow, 0.000));
	ctx.fillStyle = glow; ctx.fillRect(0,0,w,h);
	ctx.save(); ctx.translate(cx, cy); ctx.rotate(-.15);
	if (mobile) ctx.globalAlpha = .5;
	for (let i = 0; i < 20; i++) {
		const angle = TAU * i / 20;
		petal(ctx, angle, radius * (1 + .04 * Math.sin(i * 2.3)), radius * .235, radius * .042, .79 + .22 * Math.sin(angle - .8), p);
	}
	ctx.rotate(.17);
	for (let i = 0; i < 13; i++) {
		const angle = TAU * i / 13;
		petal(ctx, angle, radius * .65, radius * .19, -radius * .045, .76 + .18 * Math.cos(angle), p);
	}
	ctx.rotate(.3);
	for (let i = 0; i < 9; i++) {
		petal(ctx, TAU * i / 9, radius * .35, radius * .12, radius * .04, .80, p);
	}
	const center = ctx.createRadialGradient(-radius*.02,-radius*.02,0,0,0,radius*.12);
	center.addColorStop(0,mixColor(p.gold, p.shadow, 0.913)); center.addColorStop(.55,mixColor(p.gold, p.shadow, 0.664)); center.addColorStop(.8,mixColor(p.gold, p.shadow, 0.291)); center.addColorStop(1,alphaColor(mixColor(p.gold, p.shadow, 0.346), 0.000));
	ctx.fillStyle=center; ctx.beginPath();ctx.arc(0,0,radius*.12,0,TAU);ctx.fill();
	ctx.restore();
	// Protect the reading area; the flower remains vivid at the outer edge.
	quietArea(ctx, w, h, p.ground,.60,.83);
	ctx.save();ctx.translate(w*.13,h*1.12);ctx.rotate(-.8);ctx.globalAlpha=.75;
	petal(ctx,0,w*.34,w*.10,-w*.04,.75, p);ctx.restore();
}

function quietArea(ctx: CanvasRenderingContext2D, w: number, h: number, ground: string, start: number, end: number) {
	const fade = ctx.createLinearGradient(w * start, 0, w * end, 0);
	fade.addColorStop(0, ground); fade.addColorStop(1, `${ground}00`);
	ctx.fillStyle = fade; ctx.fillRect(0, 0, w, h);
}

function facet(ctx: CanvasRenderingContext2D, points: {x: number; y: number}[], base: number[], light: number, opacity = 1) {
	const first = points[0], last = points[points.length - 1];
	const fill = ctx.createLinearGradient(first.x, first.y, last.x, last.y);
	fill.addColorStop(0, shaded(base, light * .72, opacity));
	fill.addColorStop(.48, shaded(base, light * 1.06, opacity));
	fill.addColorStop(1, shaded(base, light * .85, opacity));
	ctx.beginPath(); points.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
	ctx.closePath(); ctx.fillStyle = fill; ctx.fill();
	ctx.lineWidth = .65; ctx.strokeStyle = shaded(base, light, opacity); ctx.stroke();
}

function drawEclipse(ctx: CanvasRenderingContext2D, w: number, h: number, scale: number, p: CampaignPalette) {
	ctx.fillStyle = p.ground; ctx.fillRect(0, 0, w, h);
	const mobile=w<=900;
	const cx=w*(mobile?1.02:.85),cy=h*(mobile?.49:.48),radius=Math.min(w*(mobile?.87:.34),h*.58)*scale;
	const halo=ctx.createRadialGradient(cx,cy,radius*.8,cx,cy,radius*1.29);
	halo.addColorStop(0,alphaColor(mixColor(p.gold, p.shadow, 0.074), 0.282));halo.addColorStop(.54,alphaColor(mixColor(p.gold, p.shadow, 0.375), 0.165));halo.addColorStop(1,alphaColor(mixColor(p.blue, p.shadow, 0.973), 0.000));
	ctx.fillStyle=halo;ctx.fillRect(0,0,w,h);
	ctx.save();if(mobile)ctx.globalAlpha=.68;
	const gold=ctx.createLinearGradient(cx-radius,cy-radius,cx+radius,cy+radius);
	gold.addColorStop(0,mixColor(p.gold, p.white, 0.559));gold.addColorStop(.22,p.gold);gold.addColorStop(.62,mixColor(p.gold, p.shadow, 0.165));gold.addColorStop(1,mixColor(p.gold, p.shadow, 0.605));
	ctx.fillStyle=gold;ctx.beginPath();ctx.arc(cx,cy,radius,0,TAU);ctx.fill();
	ctx.strokeStyle=mixColor(p.gold, p.white, 0.568);ctx.lineWidth=1.5;ctx.stroke();
	const cover=ctx.createLinearGradient(cx-radius*.2,cy-radius,cx+radius,cy+radius);
	cover.addColorStop(0,mixColor(p.shadow, p.white, 0.130));cover.addColorStop(.52,mixColor(p.blue, p.shadow, 0.986));cover.addColorStop(1,mixColor(p.white, p.shadow, 0.921));
	ctx.save();ctx.shadowColor=alphaColor(p.shadow, 0.690);ctx.shadowBlur=radius*.1;ctx.shadowOffsetX=-radius*.04;
	ctx.fillStyle=cover;ctx.beginPath();ctx.ellipse(cx+radius*.39,cy-radius*.08,radius*.90,radius*1.05,-.22,0,TAU);ctx.fill();ctx.restore();
	ctx.strokeStyle=alphaColor(p.gold, 0.251);ctx.lineWidth=1;
	ctx.beginPath();ctx.ellipse(cx,cy,radius*1.09,radius*1.09,0,.5,4.5);ctx.stroke();
	ctx.restore();
	quietArea(ctx, w, h, p.ground,.29,.57);
}

function drawContour(ctx: CanvasRenderingContext2D, w: number, h: number, scale: number, p: CampaignPalette) {
	ctx.fillStyle = p.ground; ctx.fillRect(0, 0, w, h);
	const mobile=w<=900,cx=w*(mobile?1.07:.86),cy=h*(mobile?.52:.48);
	const radiusX=w*(mobile?1.1:.42)*scale,radiusY=h*.73*scale;
	ctx.save();ctx.translate(cx,cy);ctx.rotate(-.22);
	if(mobile)ctx.globalAlpha=.6;
	for(let layer=0;layer<24;layer++){
		const inset=1-layer*.031;
		ctx.beginPath();
		for(let sample=0;sample<=220;sample++){
			const angle=sample/220*TAU;
			const ripple=1+.12*Math.sin(angle*3+.5)+.038*Math.sin(angle*7-layer*.06);
			const x=Math.cos(angle)*radiusX*inset*ripple+layer*w*.0025;
			const y=Math.sin(angle)*radiusY*inset*ripple;
			if(sample===0)ctx.moveTo(x,y);else ctx.lineTo(x,y);
		}
		ctx.closePath();
		const depth=layer/23;
		const base = rgbChannels(mixColor(p.white, p.blue, depth));
		const paper=ctx.createLinearGradient(-radiusX,-radiusY,radiusX,radiusY);
		paper.addColorStop(0,shaded(base,1.09));paper.addColorStop(1,shaded(base,.88));
		ctx.fillStyle=paper;ctx.shadowColor=alphaColor(mixColor(p.blue, p.shadow, 0.446), 0.282);ctx.shadowBlur=w*.009;ctx.shadowOffsetY=h*.007;ctx.fill();
		ctx.shadowBlur=0;ctx.shadowOffsetY=0;ctx.strokeStyle=alphaColor(p.white, 0.612);ctx.lineWidth=1;ctx.stroke();
	}
	ctx.restore();quietArea(ctx, w, h, p.ground,.33,.55);
}

function drawCrumple(ctx: CanvasRenderingContext2D, w: number, h: number, scale: number, p: CampaignPalette) {
	ctx.fillStyle = p.ground; ctx.fillRect(0, 0, w, h);
	const mobile=w<=900,cx=w*(mobile?1.08:.87),cy=h*.47;
	const size=Math.min(w*(mobile?1.15:.48),h*.92)*scale;
	const divisions=15,vertices=[];
	for(let row=0;row<=divisions;row++){
		const line=[];
		for(let column=0;column<=divisions;column++){
			const u=column/divisions*2-1,v=row/divisions*2-1;
			const x=u+.115*Math.sin(v*6.2+u*2.1),y=v+.11*Math.cos(u*7.7-v*2.8);
			const z=.19*Math.sin(u*11.4)*Math.cos(v*9.3)+.10*Math.sin((u+v)*21.1)+u*.18;
			const rx=x*.84+z*.55,rz=z*.84-x*.55;
			const ry=y*.85-rz*.53;
			line.push({x:cx+(rx*.96-ry*.28)*size*.57,y:cy+(rx*.28+ry*.96)*size*.57,z:rz*.85+y*.53});
		}vertices.push(line);
	}
	const triangles=[];
	for(let row=0;row<divisions;row++)for(let column=0;column<divisions;column++){
		const a=vertices[row][column],b=vertices[row][column+1],c=vertices[row+1][column],d=vertices[row+1][column+1];
		triangles.push([a,b,c],[b,d,c]);
	}
	triangles.sort((a,b)=>a.reduce((sum,p)=>sum+p.z,0)-b.reduce((sum,p)=>sum+p.z,0));
	ctx.save();if(mobile)ctx.globalAlpha=.62;
	for(const points of triangles){
		const [a,b,c]=points;
		const ux=(b.x-a.x)/size,uy=(b.y-a.y)/size,uz=b.z-a.z;
		const vx=(c.x-a.x)/size,vy=(c.y-a.y)/size,vz=c.z-a.z;
		const nx=uy*vz-uz*vy,ny=uz*vx-ux*vz,nz=ux*vy-uy*vx;
		const length=Math.hypot(nx,ny,nz)||1;
		const lighting=Math.abs((nx*-.34+ny*-.63+nz*.70)/length);
		const shine=Math.pow(Math.max(0,(-nx*.12-ny*.40+nz*.91)/length),7);
		const light=.21+lighting*.97+shine*.68;
		facet(ctx,points,rgbChannels(p.gold),light);
		if(shine>.2){ctx.strokeStyle=alphaColor(mixColor(p.gold, p.white, .8), Math.min(.8, shine));ctx.lineWidth=.9;ctx.beginPath();ctx.moveTo(a.x,a.y);ctx.lineTo(b.x,b.y);ctx.stroke();}
	}
	ctx.restore();quietArea(ctx, w, h, p.ground,.32,.58);
}

function paper(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, angle: number, fill: string, p: CampaignPalette) {
	ctx.save();ctx.translate(x,y);ctx.rotate(angle);
	ctx.lineWidth=4;ctx.strokeStyle=mixColor(p.blue, p.shadow, 0.132);ctx.lineJoin='round';ctx.fillStyle=fill;
	ctx.beginPath();ctx.roundRect(-width/2,-height/2,width,height,18);ctx.fill();ctx.stroke();
	ctx.strokeStyle=mixColor(p.blue, p.white, 0.380);ctx.lineWidth=3;
	for(let line=0;line<3;line++){
		ctx.beginPath();ctx.moveTo(-width*.32,-height*.21+line*height*.10);ctx.lineTo(width*(line===2?.06:.28),-height*.21+line*height*.10);ctx.stroke();
	}
	ctx.restore();
}

function drawCartoon(ctx: CanvasRenderingContext2D, w: number, h: number, scale: number, p: CampaignPalette) {
	ctx.fillStyle = p.ground; ctx.fillRect(0, 0, w, h);
	const mobile=w<=900;
	const size=Math.min(w*(mobile?1.1:.50),h*.84)*scale;
	const cx=w*(mobile?.93:.84),cy=h*.47;
	ctx.save();
	if(mobile)ctx.globalAlpha=.82;
	// An oversized folder and paper forms make the illustration specific to sending files.
	paper(ctx,cx-size*.15,cy-size*.10,size*.52,size*.68,-.22,mixColor(p.blue, p.white, 0.874), p);
	paper(ctx,cx+size*.14,cy-size*.18,size*.52,size*.68,.12,p.white, p);
	ctx.save();ctx.translate(cx,cy+size*.20);ctx.rotate(-.06);
	ctx.fillStyle=p.gold;ctx.strokeStyle=mixColor(p.blue, p.shadow, 0.132);ctx.lineWidth=4;ctx.lineJoin='round';
	ctx.beginPath();ctx.moveTo(-size*.48,-size*.15);ctx.lineTo(-size*.18,-size*.15);ctx.lineTo(-size*.10,-size*.07);ctx.lineTo(size*.48,-size*.07);
	ctx.quadraticCurveTo(size*.53,-size*.07,size*.51,size*.01);ctx.lineTo(size*.45,size*.41);ctx.quadraticCurveTo(size*.45,size*.46,size*.39,size*.46);
	ctx.lineTo(-size*.39,size*.46);ctx.quadraticCurveTo(-size*.45,size*.46,-size*.46,size*.40);ctx.lineTo(-size*.51,-size*.08);ctx.quadraticCurveTo(-size*.52,-size*.15,-size*.48,-size*.15);ctx.closePath();ctx.fill();ctx.stroke();
	ctx.beginPath();ctx.moveTo(-size*.38,size*.05);ctx.lineTo(size*.37,size*.05);ctx.strokeStyle=mixColor(p.gold, p.shadow, 0.202);ctx.lineWidth=2;ctx.stroke();
	ctx.restore();
	// One paper plane, not an imitation of the product's actual upload controls.
	ctx.save();ctx.translate(w*.93,h*.17);ctx.rotate(-.23);
	const plane=size*.28;
	ctx.fillStyle=p.white;ctx.strokeStyle=mixColor(p.blue, p.shadow, 0.132);ctx.lineWidth=4;ctx.lineJoin='round';
	ctx.beginPath();ctx.moveTo(-plane,plane*.08);ctx.lineTo(plane,-plane*.45);ctx.lineTo(plane*.43,plane*.72);ctx.lineTo(-plane*.06,plane*.30);ctx.closePath();ctx.fill();ctx.stroke();
	ctx.beginPath();ctx.moveTo(-plane*.06,plane*.30);ctx.lineTo(plane,-plane*.45);ctx.moveTo(-plane*.06,plane*.30);ctx.lineTo(-plane*.15,plane*.70);ctx.lineTo(plane*.12,plane*.47);ctx.stroke();
	ctx.restore();
	ctx.strokeStyle=mixColor(p.blue, p.shadow, 0.132);ctx.lineWidth=2.2;ctx.setLineDash([7,9]);
	ctx.beginPath();ctx.moveTo(w*.52,-h*.06);ctx.bezierCurveTo(w*.49,h*.17,w*.64,h*.25,w*.70,h*.09);ctx.bezierCurveTo(w*.75,-h*.03,w*.81,h*.13,w*.82,h*.19);ctx.stroke();ctx.setLineDash([]);
	ctx.save();ctx.translate(w*.63,h*.36);ctx.rotate(.1);ctx.fillStyle=p.gold;ctx.strokeStyle=mixColor(p.blue, p.shadow, 0.132);ctx.lineWidth=3;
	const star=size*.07;ctx.beginPath();
	for(let point=0;point<10;point++){const angle=point/10*TAU-Math.PI/2,radius=point%2===0?star:star*.46;const x=Math.cos(angle)*radius,y=Math.sin(angle)*radius;point?ctx.lineTo(x,y):ctx.moveTo(x,y);}
	ctx.closePath();ctx.fill();ctx.stroke();ctx.restore();
	ctx.restore();
}

function drawNewspaper(ctx: CanvasRenderingContext2D, w: number, h: number, scale: number, p: CampaignPalette, geometry?: CampaignArtworkGeometry) {
	ctx.fillStyle = p.ground; ctx.fillRect(0, 0, w, h);
	const {readingTop, readingRight, composerLeft} = studyGeometry(w, h, geometry);
	const mobile = w <= 900;
	const margin = 16 * scale;
	const left = mobile ? margin : readingRight + 28 * scale;
	const right = w - (mobile ? margin : w * .04);
	const top = Number.parseFloat(w <= 640 ? topbar.clearanceMobile : topbar.clearance) + margin;
	const bandHeight = 30 * scale;
	ctx.save();
	if (right > left && (!mobile || top + bandHeight < readingTop)) {
		ctx.fillStyle = p.ink;
		ctx.fillRect(left, top, right - left, 3 * scale);
		ctx.fillRect(left, top + bandHeight, right - left, scale);
		if (geometry) {
			ctx.font = `small-caps 400 ${12 * scale}px ${fontFamilies.displaySerif}`;
			ctx.textBaseline = "middle";
			ctx.textAlign = "left";
			ctx.fillText(geometry.copy.newspaper.masthead, left, top + bandHeight / 2 + scale, (right - left) * .58);
			ctx.textAlign = "right";
			ctx.fillText(geometry.copy.newspaper.issue, right, top + bandHeight / 2 + scale, (right - left) * .38);
		}
	}

	const columnLeft = mobile ? (geometry ? Math.max(0, composerLeft - 8 * scale) : w * .06) : left;
	const columnRight = mobile ? w - columnLeft : right;
	const columnTop = mobile ? Math.max(top + bandHeight + margin, geometry?.composerTop ?? h * .24) : top + 48 * scale;
	const columnBottom = mobile ? Math.min(readingTop - 28 * scale, geometry?.composerBottom ?? h * .6) : h * .88;
	const count = mobile ? 1 : 3;
	const gutter = 24 * scale;
	const columnWidth = (columnRight - columnLeft - gutter * (count - 1)) / count;
	// Fixed paragraph endings give the texture a newsprint rhythm without words or paint-time randomness.
	const endings = [.62, .81, .47, .73, .56];
	const blocks = [.19, .28, .13, .23, .17];
	if (columnWidth > 0 && columnBottom > columnTop) {
		for (let column = 0; column < count; column++) {
			const x = columnLeft + column * (columnWidth + gutter);
			ctx.fillStyle = alphaColor(p.ink, mobile ? .075 : .12);
			for (let row = 0, y = columnTop; y + 2 * scale <= columnBottom; row++, y += 6 * scale) {
				const paragraphRow = row % 9;
				if (paragraphRow === 8) continue;
				const length = paragraphRow === 7 ? endings[(Math.floor(row / 9) + column) % endings.length] : 1;
				const space = Math.min(3 * scale, columnWidth / 20);
				const lineWidth = columnWidth * length - space * (blocks.length - 1);
				let blockLeft = x;
				for (let block = 0; block < blocks.length; block++) {
					const width = lineWidth * blocks[(block + row + column) % blocks.length];
					ctx.fillRect(blockLeft, y, width, 2 * scale);
					blockLeft += width + space;
				}
			}
			if (column < count - 1) {
				ctx.fillStyle = alphaColor(p.ink, .16);
				ctx.fillRect(x + columnWidth + gutter / 2, columnTop, scale, columnBottom - columnTop);
			}
		}
	}
	if (!mobile) {
		ctx.fillStyle = mixColor(p.ground, p.ink, .035);
		ctx.fillRect(w * .975, 0, w * .025, h);
		const corner = 24 * scale;
		ctx.strokeStyle = alphaColor(p.ink, .4); ctx.lineWidth = scale;
		ctx.beginPath(); ctx.moveTo(w - margin - corner, h - margin); ctx.lineTo(w - margin, h - margin); ctx.lineTo(w - margin, h - margin - corner); ctx.stroke();
	}
	ctx.restore();
}

function drawCover(ctx: CanvasRenderingContext2D, w: number, h: number, scale: number, p: CampaignPalette) {
	ctx.fillStyle = p.ground; ctx.fillRect(0, 0, w, h);
	const mobile=w<=900;
	const cx=w*(mobile?1.26:1.05),cy=h*.53;
	const rx=w*.38*scale,ry=h*.64*scale;
	ctx.save();ctx.translate(cx,cy);ctx.rotate(-.18);
	ctx.fillStyle=mixColor(p.blue, p.white, 0.056);ctx.beginPath();ctx.ellipse(0,0,rx,ry,0,0,Math.PI*2);ctx.fill();
	ctx.strokeStyle=alphaColor(mixColor(p.blue, p.white, 0.680), 0.349);ctx.lineWidth=1;
	ctx.beginPath();ctx.ellipse(0,0,rx*1.09,ry*1.06,0,0,Math.PI*2);ctx.stroke();
	ctx.restore();
	ctx.strokeStyle=alphaColor(mixColor(p.gold, p.white, 0.946), 0.231);ctx.lineWidth=1;
	ctx.beginPath();ctx.moveTo(w*.046,h*.94);ctx.lineTo(w*.64,h*.94);ctx.stroke();
}


export const campaignRenderers: Record<CampaignId, (ctx: CanvasRenderingContext2D, width: number, height: number, scale: number, palette: CampaignPalette, geometry?: CampaignArtworkGeometry) => void> = {bloom: drawBloom, eclipse: drawEclipse, contour: drawContour, crumple: drawCrumple, cartoon: drawCartoon, newspaper: drawNewspaper, cover: drawCover, ink: drawInk, showcase: drawShowcase, kinetic: drawKinetic, cutpaper: drawCutpaper, collage: drawCollage, pumpkin: editionSlide("pumpkin"), vampire: editionSlide("vampire"), zombie: editionSlide("zombie"), witch: editionSlide("witch")};

/** The editions whose artwork moves, repainted every frame while the slide plays (never under reduced motion). */
export const ANIMATED_EDITIONS: ReadonlySet<CampaignId> = new Set<CampaignId>(["ink", "kinetic", ...HALLOWEEN_EDITIONS]);

